import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Spin, Col, Row, Statistic, Progress } from 'antd';
import {
  SolutionOutlined,
  BugOutlined,
  HourglassOutlined,
  FieldTimeOutlined,
  LikeOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
} from '@ant-design/icons';

import { handleTaskCounter } from '~/pages/Task/Card/functions';
import { projectDashboardRequest } from '~/store/modules/projectDashboard/actions';

import { Container, Content, NoContent, Card, SprintInfo, ListItem, Empty, BarRow } from './styles';

const formatTime = (seconds) => handleTaskCounter(Number(seconds) || 0, '00h00');
const formatDate = (value) => (value ? String(value).substring(0, 10).split('-').reverse().join('/') : '');

const STATES = [
  { key: 'queue', label: 'Na fila' },
  { key: 'development', label: 'Em desenvolvimento' },
  { key: 'blocked', label: 'Bloqueadas' },
  { key: 'review', label: 'Em revisão' },
  { key: 'done', label: 'Concluídas' },
];

export default function Dashboard(props) {
  const dispatch = useDispatch();

  const projectId = props.match.params.id;

  const loading = useSelector(store => store.projectDashboard.loading);
  const result = useSelector(store => store.projectDashboard.registry);

  useEffect(() => {
    dispatch(projectDashboardRequest(projectId));
  }, [projectId]);

  const sprint = result?.sprint;
  const tasks = result?.tasks;
  const progress = result?.progress;

  const taskLink = (taskId) => `/projects/${projectId}/sprints/${sprint?.id}/tasks/${taskId}`;

  const renderTasks = (title, list, detail) => (
    <Col xs={24} md={12}>
      <Card bordered={true}>
        <h2>{title}</h2>
        {!list || list.length === 0 ? (
          <Empty>Nenhuma tarefa</Empty>
        ) : (
          list.map(item => (
            <ListItem key={item.id}>
              <Link to={taskLink(item.id)}>#{item.index} {item.name}</Link>
              {detail && <span>{detail(item)}</span>}
            </ListItem>
          ))
        )}
      </Card>
    </Col>
  );

  const renderDiff = (value, isTime) => {
    const diff = Number(value) || 0;
    const text = isTime ? formatTime(Math.abs(diff)) : Math.abs(diff);
    return diff >= 0
      ? <span style={{ color: '#3f8600' }}> <ArrowUpOutlined /> {text}</span>
      : <span style={{ color: '#cf1322' }}> <ArrowDownOutlined /> {text}</span>;
  };

  const renderBars = (list, labelFn) => {
    const max = Math.max(...(list || []).map(i => Number(i.workedSeconds)), 0);
    return (list || []).map(item => (
      <BarRow key={labelFn(item)}>
        <div className="label">
          <b>{labelFn(item)}</b>
          <span>{formatTime(item.workedSeconds)}</span>
        </div>
        <Progress percent={max > 0 ? (Number(item.workedSeconds) / max) * 100 : 0} showInfo={false} size="small" />
      </BarRow>
    ));
  };

  return (
    <Container>
      {loading || !result ? (
        <NoContent>
          <Spin />
        </NoContent>
      ) : (
        <Content>
          <Row style={{ marginBottom: '1rem' }}>
            <Col xs={24}>
              <h1>
                {result.project?.name}
                {sprint && (
                  <SprintInfo>
                    Sprint {sprint.name} ({formatDate(sprint.startAt)} a {formatDate(sprint.endAt)}) - {sprint.daysLeft} dia(s) restante(s)
                  </SprintInfo>
                )}
              </h1>
            </Col>
          </Row>

          {!sprint ? (
            <Empty>Este projeto ainda não possui uma linha do tempo (sprint).</Empty>
          ) : (
            <>
              <Row gutter={16}>
                <Col xs={12} sm={8} md={6} xl={4}>
                  <Card bordered={true}>
                    <Statistic title="Tarefas na sprint" value={tasks.tasks} valueStyle={{ color: 'var(--card-task-border)' }} prefix={<SolutionOutlined />} />
                  </Card>
                </Col>
                <Col xs={12} sm={8} md={6} xl={4}>
                  <Card bordered={true}>
                    <Statistic title="Bugs na sprint" value={tasks.bugs} valueStyle={{ color: '#cf1322' }} prefix={<BugOutlined />} />
                  </Card>
                </Col>
                <Col xs={12} sm={8} md={6} xl={4}>
                  <Card bordered={true}>
                    <Statistic title="Horas planejadas" value={formatTime(progress.plannedSeconds)} valueStyle={{ color: 'var(--project-icon-backlog)' }} prefix={<FieldTimeOutlined />} />
                  </Card>
                </Col>
                <Col xs={12} sm={8} md={6} xl={4}>
                  <Card bordered={true}>
                    <Statistic title="Horas trabalhadas" value={formatTime(progress.workedSeconds)} valueStyle={{ color: progress.overBudget ? '#cf1322' : '#3f8600' }} prefix={<HourglassOutlined />} />
                  </Card>
                </Col>
                <Col xs={12} sm={8} md={6} xl={4}>
                  <Card bordered={true}>
                    <Statistic title="Cronômetros rodando" value={result.running?.length || 0} valueStyle={{ color: '#3f8600' }} prefix={<LikeOutlined />} />
                  </Card>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Card bordered={true}>
                    <h2>Horas trabalhadas x planejadas</h2>
                    <Progress
                      percent={Math.min(progress.percent, 100)}
                      status={progress.overBudget ? 'exception' : 'active'}
                      format={() => `${progress.percent}%`}
                    />
                    <Empty>{formatTime(progress.workedSeconds)} de {formatTime(progress.plannedSeconds)}</Empty>
                  </Card>
                </Col>
                <Col xs={24} md={12}>
                  <Card bordered={true}>
                    <h2>Andamento das tarefas ({tasks.donePercent}% concluídas)</h2>
                    {STATES.map(s => (
                      <BarRow key={s.key}>
                        <div className="label"><b>{s.label}</b><span>{tasks.byState[s.key]}</span></div>
                        <Progress percent={tasks.total > 0 ? (tasks.byState[s.key] / tasks.total) * 100 : 0} showInfo={false} size="small" />
                      </BarRow>
                    ))}
                  </Card>
                </Col>
              </Row>

              <Row gutter={16}>
                {renderTasks('Atrasadas', result.risks?.overdue, i => formatDate(i.deadlineAt))}
                {renderTasks('Prazo nos próximos 7 dias', result.risks?.dueSoon, i => formatDate(i.deadlineAt))}
                {renderTasks('Estouraram a estimativa', result.risks?.overBudget, i => `${formatTime(i.workedSeconds)} / ${formatTime(i.remainingWork * 60)}`)}
                {renderTasks('Sem estimativa', result.risks?.noEstimate)}
                {renderTasks('Bloqueadas', result.blocked)}
                <Col xs={24} md={12}>
                  <Card bordered={true}>
                    <h2>Rodando agora</h2>
                    {!result.running || result.running.length === 0 ? (
                      <Empty>Nenhum cronômetro rodando</Empty>
                    ) : (
                      result.running.map(item => (
                        <ListItem key={`${item.taskId}-${item.userId}`}>
                          <Link to={taskLink(item.taskId)}>#{item.index} {item.name}</Link>
                          <span>{item.userName}</span>
                        </ListItem>
                      ))
                    )}
                  </Card>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Card bordered={true}>
                    <h2>Horas por colaborador</h2>
                    {result.hoursByUser?.length ? renderBars(result.hoursByUser, i => i.name) : <Empty>Sem horas lançadas</Empty>}
                  </Card>
                </Col>
                <Col xs={24} md={12}>
                  <Card bordered={true}>
                    <h2>Horas por dia</h2>
                    {result.hoursByDay?.length ? renderBars(result.hoursByDay, i => formatDate(i.day)) : <Empty>Sem horas lançadas</Empty>}
                  </Card>
                </Col>
              </Row>

              {result.previousSprint && (
                <Row gutter={16}>
                  <Col xs={24}>
                    <Card bordered={true}>
                      <h2>Comparativo com a sprint anterior ({result.previousSprint.name})</h2>
                      <Row gutter={16}>
                        <Col xs={12} md={6}>
                          <Statistic title="Tarefas concluídas" value={tasks.byState.done} suffix={renderDiff(result.previousSprint.tasksDoneDiff)} />
                        </Col>
                        <Col xs={12} md={6}>
                          <Statistic title="Horas trabalhadas" value={formatTime(progress.workedSeconds)} suffix={renderDiff(result.previousSprint.workedSecondsDiff, true)} />
                        </Col>
                      </Row>
                    </Card>
                  </Col>
                </Row>
              )}
            </>
          )}
        </Content>
      )}
    </Container>
  );
}
